<script setup lang="ts">
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhButton,
    ZhConfirmButton,
    ZhEmptyState,
    ZhPageTitle,
    ZhPanel,
} from '@zerohuman/ui'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import {
    api,
    type Blockers,
    type KpiHealthRow,
    type RaisedWorkItem,
    type RunRow,
    stoppedReason,
    type TaskInputField,
    type Team,
} from '@/api'
import BlockerHeading from '@/components/BlockerHeading.vue'
import DataTable, { type DataTableColumn } from '@/components/DataTable.vue'
import DefinitionIssueLine from '@/components/DefinitionIssueLine.vue'
import RetryCorrectionForm from '@/components/RetryCorrectionForm.vue'
import { useListState } from '@/composables/useListState'
import { requireAiAccount } from '@/lib/aiAccountGuard'
import {
    autoBlockedRuns,
    type BlockerFix,
    type BlockerKpiRow,
    type BlockerMessage,
    type BlockerSection,
    blockedConnectionWhose,
    blockerAlertTotal,
    blockerCounts,
    blockerKpiRows,
    capacityMessage,
    escalationRuns,
    lastPage,
    lostLogin,
    planPausedRuns,
    runBlockerMessage,
    spendPausedRuns,
    unmappedActions,
} from '@/lib/blockers'
import { type RetryValues, refusedField, retryCorrection, retryFields, retryValues } from '@/lib/retryCorrection'

/**
 * #633: Blockers has no search, filter, tab, sort or mine/everyone choice to keep (its page number and size are never
 * kept), so it opts into the shared list state with no fields: a choice added here later is kept like every other
 * list page's.
 */
useListState('blockers', {})

const router = useRouter()

/** A run row with what the page says about it: who, what happened and what fixes it (#1772). */
type MessagedRun = RunRow & { message: BlockerMessage }

type RunGroup = {
    key: BlockerSection
    title: string
    rows: MessagedRun[]
    /** Failed: paged server-side, with Clear all. */
    paged?: boolean
}

/**
 * One Team health row: a team, the sentence explaining what it is missing, and for a KPI the details behind it.
 * A company proposal's row has no team (#366), so it links nowhere.
 */
type TeamHealthRow = {
    key: string
    team: Team | null
    message: string
    /** A KPI row's layer, light, trend and whose move it is. */
    detail?: string
}

const LIGHT_WORD: Record<NonNullable<KpiHealthRow['light']>, string> = { red: 'RED', amber: 'AMBER', green: 'GREEN' }
const TREND_ARROW: Record<NonNullable<KpiHealthRow['trend']>['direction'], string> = { up: '↑', down: '↓', flat: '→' }
const MOVER: Record<KpiHealthRow['owner']['role'], string> = {
    team_lead: 'Team lead’s move',
    coo: 'COO’s move (acting lead)',
    ceo: 'Waiting on the CEO’s decision',
}

/** What a KPI row says is wrong, in one sentence. */
function kpiHealthMessage(row: BlockerKpiRow): string {
    const kpi = row.kpi?.name ?? 'Its key KPI'
    const owner = row.team?.name ?? 'Company'
    switch (row.condition) {
        case 'no_key_kpi':
            return `${owner} has no key KPI`
        case 'no_target':
            return `${owner}: ${kpi} has no target`
        case 'overdue':
            return `${owner}: ${kpi} is overdue for a reading`
        case 'awaiting_decision':
            return `${owner}: a proposal for ${kpi} is waiting on the CEO`
    }
}

/** The KPI's layer, its light and trend, and whose move it is: the team lead, the COO while acting lead, or the CEO. */
function kpiHealthDetail(row: KpiHealthRow): string {
    const parts: string[] = []
    if (row.layer) {
        parts.push(`${row.layer} layer`)
    }
    if (row.light) {
        parts.push(row.condition === 'overdue' ? `last read ${LIGHT_WORD[row.light]}` : LIGHT_WORD[row.light])
    }
    if (row.trend) {
        parts.push(`${TREND_ARROW[row.trend.direction]} ${row.trend.percent}%`)
    }
    parts.push(MOVER[row.owner.role])
    return parts.join(' · ')
}

const blockers = ref<Blockers | null>(null)
const error = ref<string | null>(null)
/** A retry the member is too busy to start now waits instead: the row goes, so say where it went. */
const notice = ref<string | null>(null)
const busy = ref<string | null>(null)
const failedPage = ref(1)
/** Rows per page for the failed table; the API answers with the size it used. */
const pageSize = ref(20)
let timer: ReturnType<typeof setInterval> | null = null

const counts = computed(() => (blockers.value ? blockerCounts(blockers.value) : null))
const empty = computed(() => !blockers.value || blockerAlertTotal(blockers.value) === 0)

/**
 * Only the groups that have something in them. An empty group was a heading, a zero and a "None." —
 * noise on a page whose whole job is to list what needs a decision. Filtering on `blockerCounts` uses
 * the same number the heading shows, so `failed` is the server-side total, not the rows on this page.
 */
const runGroups = computed((): RunGroup[] => {
    const value = blockers.value
    const totals = counts.value
    if (!value || !totals) {
        return []
    }
    const group = (key: BlockerSection, title: string, rows: RunRow[], paged?: boolean): RunGroup => ({
        key,
        title,
        rows: rows.map(run => ({
            ...run,
            message: runBlockerMessage(key, run, value.decisionGates?.[run.id] ?? null),
        })),
        paged,
    })
    const groups: RunGroup[] = [
        group('escalations', 'Needs a decision', escalationRuns(value)),
        group('stopped', 'Pipeline stopped', value.stopped ?? []),
        group('blocked', 'Blocked (resumes automatically)', autoBlockedRuns(value)),
        group('failed', 'Failed', value.failed, true),
        group('stalled', 'Stalled', value.stalled),
    ]
    if (value.spendEnabled !== false) {
        groups.push(group('paused', 'Paused spend', spendPausedRuns(value)))
    }
    // Its own heading, whatever the Spend switch says: the plan is not the CEO's cap (#445).
    groups.push(group('plan', 'Paused: plan limit', planPausedRuns(value)))
    return groups.filter(entry => totals[entry.key] > 0)
})

/** Each exhausted connection in words: which limit, when it resets, and the fix for it (#1772). */
const capacityCards = computed(() =>
    (blockers.value?.capacity ?? []).map(entry => ({ connectionId: entry.connectionId, ...capacityMessage(entry) })),
)

/**
 * Team health rows: one per team missing a lead or overdue on its weekly retro, then one per KPI that needs
 * attention (#899), leaving out a KPI that is only AMBER or RED (`blockerKpiRows`). A single list (like `runGroups` above) instead of one `v-for` per kind, so a new
 * team-health check is one more entry here rather than another near-identical `<article>` block in the
 * template. The deprecated `missingMetric` list is not shown.
 */
const teamHealthRows = computed((): TeamHealthRow[] => {
    const value = blockers.value
    if (!value) {
        return []
    }
    const sections: { prefix: string; teams: Team[]; message: (team: Team) => string }[] = [
        { prefix: 'lead', teams: value.missingLead ?? [], message: team => `${team.name} has no lead` },
        {
            prefix: 'retro',
            teams: value.overdueRetro ?? [],
            message: team => `${team.name} has not filed its weekly retro`,
        },
    ]
    const teamRows = sections.flatMap(section =>
        section.teams.map(team => ({ key: `${section.prefix}-${team.id}`, team, message: section.message(team) })),
    )
    // The condition is in the key: a team KPI can be overdue and have a proposal waiting on the CEO at once.
    const kpiRows = blockerKpiRows(value).map(row => ({
        key: `kpi-${row.condition}-${row.team?.id ?? 'company'}-${row.kpi?.id ?? 'none'}`,
        team: row.team,
        message: kpiHealthMessage(row),
        detail: kpiHealthDetail(row),
    }))
    return [...teamRows, ...kpiRows]
})

onMounted(() => {
    void load()
    timer = setInterval(() => {
        void load()
    }, 2000)
})

onUnmounted(() => {
    if (timer) {
        clearInterval(timer)
    }
})

async function load(): Promise<void> {
    try {
        const value = await api.blockers({ failedPage: failedPage.value, pageSize: pageSize.value })
        blockers.value = value
        // The API clamps a page that no longer exists (for example after clearing).
        failedPage.value = value.failedPage ?? 1
        pageSize.value = value.pageSize ?? pageSize.value
        error.value = null
    } catch {
        error.value = 'Could not load blockers.'
    }
}

async function act(id: string, work: () => Promise<unknown>, failure: string): Promise<void> {
    busy.value = id
    notice.value = null
    try {
        await work()
        await load()
    } catch (caught) {
        error.value = caught instanceof Error ? `${failure} (${caught.message}).` : `${failure}.`
    } finally {
        busy.value = null
    }
}

function cancel(id: string): Promise<void> {
    return act(id, () => api.cancelRun(id), 'Could not cancel')
}

/** A fix the page performs on the run itself, rather than a link to where the fix is made. */
function perform(fix: BlockerFix, id: string, section: BlockerSection): Promise<void> {
    return fix.action === 'cancel' ? cancel(id) : retry(id, section)
}

/** Who the run worked as: the teammate, or its team when it worked as no member. Null when it had neither. */
function workedAs(run: MessagedRun): string | null {
    if (!run.message.teammate) {
        return null
    }
    return run.assigneeName ? `Teammate: ${run.message.teammate}` : `Team: ${run.message.teammate}`
}

/** The failed table's rows are the group's own rows; the table reads them as plain records. */
function asMessaged(row: Record<string, unknown>): MessagedRun {
    return row as unknown as MessagedRun
}

/**
 * The retry correction form (#1337), the one Run detail offers: the run it retries, the section its Retry was
 * pressed in (the form opens there), and the fields that run's task declares. One run at a time.
 */
const retrying = ref<{ id: string; section: BlockerSection; inputs: TaskInputField[] } | null>(null)
const retryForm = ref<RetryValues>({})
/** The server's field-level refusal, against the field it names. */
const retryErrors = ref<Record<string, string>>({})

/**
 * Retry reads the run first, for the inputs its task declares. A task that declares some gets the correction form,
 * prefilled from the run's stored input, so a run stopped by one of its inputs is not started again with it. A task
 * that declares none, or a run that cannot be read for them, is retried at once with its stored input, as before.
 */
async function retry(id: string, section: BlockerSection): Promise<void> {
    busy.value = id
    notice.value = null
    retrying.value = null
    const detail = await api.run(id).catch(() => null)
    if (detail && retryFields(detail.inputs).length > 0) {
        retryForm.value = retryValues(detail.inputs, detail.run.input)
        retryErrors.value = {}
        retrying.value = { id, section, inputs: detail.inputs ?? [] }
        busy.value = null
        return
    }
    await sendRetry(id, null)
}

function submitRetry(): Promise<void> {
    const open = retrying.value
    if (!open) {
        return Promise.resolve()
    }
    return sendRetry(open.id, retryCorrection(open.inputs, retryForm.value))
}

/**
 * Starts the retry: plain, or with the form's correction. A pending lap says where it went, and the list reloads
 * so a superseded row can leave. A refusal naming a field the form shows stays on the form, values kept; any
 * other refusal is the page's error, as a plain retry's always was.
 */
async function sendRetry(id: string, correction: Record<string, unknown> | null): Promise<void> {
    // #1796: a retry starts model work, so without an AI account nothing is sent and the prompt opens.
    if (!(await requireAiAccount())) return
    busy.value = id
    notice.value = null
    retryErrors.value = {}
    try {
        const next = correction ? await api.retryRun(id, correction) : await api.retryRun(id)
        retrying.value = null
        if (next.status === 'pending') {
            notice.value = 'Retry is queued: it starts when the member is free.'
        }
        await load()
    } catch (caught) {
        const field = refusedField(caught)
        const open = retrying.value
        if (open?.id === id && field && retryFields(open.inputs).some(input => input.key === field)) {
            retryErrors.value = { [field]: caught instanceof Error ? caught.message : String(caught) }
        } else {
            error.value = caught instanceof Error ? `Could not retry (${caught.message}).` : 'Could not retry.'
        }
    } finally {
        busy.value = null
    }
}

const clearing = ref<string | null>(null)
const clearWorkItemState = ref<'held' | 'ready' | 'done' | 'dropped'>('held')
function clearRun(id: string): void {
    clearWorkItemState.value = 'held'
    clearing.value = id
}
async function submitClear(): Promise<void> {
    const id = clearing.value
    if (!id) return
    await act(id, () => api.clearRun(id, clearWorkItemState.value), 'Could not clear')
    clearing.value = null
}

function clearAll(): Promise<void> {
    // ZhConfirmButton has already asked — a second native prompt would make the user answer twice.
    return act('clear-failed', () => api.clearBlockers('failed'), 'Could not clear all')
}

/** #431: the raised findings have been dealt with (a team took them on, or a person added the item by hand). */
function clearRaised(): Promise<void> {
    return act('clear-raised', () => api.clearBlockers('raised'), 'Could not clear the findings')
}

/** #431: one raised finding has been dealt with: only it leaves Blockers, the others stay. */
function clearRaisedOne(id: string): Promise<void> {
    return act(`raised:${id}`, () => api.clearBlockers('raised', id), 'Could not clear the finding')
}

/** #431: why a finding came to a person: no team holds the function it was sent to, or several do. */
function raisedWhy(entry: RaisedWorkItem): string {
    if (entry.reason === 'several_matching_teams') {
        const names = entry.candidates.map(candidate => candidate.name).join(', ')
        return `Several teams hold the ${entry.forFunction} function (${names}), so it was not added to any of their lists.`
    }
    return `No team holds the ${entry.forFunction} function, so it was not added to any list.`
}

/** #431: what the person does with a raised finding: the OS placed it nowhere, so they choose the list. */
function raisedNext(entry: RaisedWorkItem): string {
    return entry.reason === 'several_matching_teams'
        ? 'Next: pick one of those teams, add this to its list yourself, then clear it here.'
        : 'Next: add this to the list of the team that should do it yourself, then clear it here.'
}

function findingCount(count: number): string {
    return `${count} ${count === 1 ? 'finding' : 'findings'}`
}

function runCount(count: number): string {
    return `${count} ${count === 1 ? 'run' : 'runs'}`
}

/** What a bulk action could not do, after what it did: the runs it refused, with the first reason given. */
function refusedWords(refused: { error: string }[], verb: string): string {
    const first = refused[0]
    return first ? ` ${runCount(refused.length)} could not be ${verb} (${first.error}).` : ''
}

/**
 * Retry every blocked run that resumes by itself (ZhConfirmButton has already asked). Like a single Retry it starts
 * model work, so without an AI account nothing is sent and the prompt opens.
 */
async function retryAllBlocked(): Promise<void> {
    if (!(await requireAiAccount())) return
    await act(
        'bulk-blocked',
        async () => {
            const result = await api.retryBlockers('blocked')
            const queued = result.queued > 0 ? `; ${result.queued} queued until their member is free` : ''
            notice.value = `Retried ${runCount(result.retried)}${queued}.${refusedWords(result.refused, 'retried')}`
        },
        'Could not retry all',
    )
}

function cancelAllBlocked(): Promise<void> {
    return act(
        'bulk-blocked',
        async () => {
            const result = await api.cancelBlockers('blocked')
            notice.value = `Cancelled ${runCount(result.cancelled)}.${refusedWords(result.refused, 'cancelled')}`
        },
        'Could not cancel all',
    )
}

const failedTotal = computed(() => blockers.value?.failedTotal ?? 0)
const failedPages = computed(() => lastPage(failedTotal.value, pageSize.value))

function goToPage(page: number): void {
    failedPage.value = Math.min(Math.max(page, 1), failedPages.value)
    void load()
}

/** A bigger or smaller page is a fresh server page, not a re-slice of what is already here. */
function setPageSize(size: number): void {
    pageSize.value = Math.max(1, size)
    failedPage.value = 1
    void load()
}

/**
 * A failed run reads as: the task and who worked it, what happened in a plain sentence, when, and what fixes it.
 * The run itself is a secondary Open run link, never the row's heading. Never the raw run id: a run can reach a
 * terminal status with no explaining event (os#316), and the id is not a reason.
 */
const runColumns: DataTableColumn[] = [
    { key: 'taskName', label: 'Task', filterable: false },
    { key: 'reason', label: 'What happened', filterable: false, class: 'max-w-sm' },
    { key: 'createdAt', label: 'Started', filterable: false },
    { key: 'actions', label: 'Actions', filterable: false, class: 'text-right' },
]
</script>

<template>
    <Dialog :open="clearing !== null" @update:open="open => { if (!open && !busy) clearing = null }">
        <DialogContent>
            <DialogHeader>
                <DialogTitle>Clear blocker</DialogTitle>
                <DialogDescription>Remove this run from Blockers and choose what happens to its work item, if it holds one.</DialogDescription>
            </DialogHeader>
            <label class="block space-y-2 text-sm">
                <span>Work item</span>
                <Select v-model="clearWorkItemState" data-testid="clear-work-item-state" :disabled="!!busy">
                    <SelectTrigger aria-label="Work item"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="held">Keep held — decide later</SelectItem>
                        <SelectItem value="done">Done — the work is complete</SelectItem>
                        <SelectItem value="dropped">Drop — no longer wanted</SelectItem>
                        <SelectItem value="ready">Ready — return to Backlog</SelectItem>
                    </SelectContent>
                </Select>
            </label>
            <p class="text-muted-foreground text-sm">Choose Done only after checking that the work is complete. Ready lets the team pick it up again.</p>
            <div class="flex gap-2">
                <Button data-testid="clear-submit" :disabled="!!busy" @click="submitClear">Clear blocker</Button>
                <Button variant="ghost" :disabled="!!busy" @click="clearing = null">Cancel</Button>
            </div>
        </DialogContent>
    </Dialog>
	<section class="w-full space-y-6 p-8">
		<ZhPageTitle>Blockers</ZhPageTitle>
		<p class="text-muted-foreground text-sm">
			COO keep-on-track: loop caps that need a decision, stopped pipelines, blocked runs, unavailable models,
			broken task definitions, failed and stalled runs<template v-if="blockers?.spendEnabled !== false">, paused spend</template>, a missing team lead, and KPIs that need attention. Everything here wants a decision or a fix. Waiting gates, including ones past an hour or aged at 24h, stay on
			<RouterLink class="underline" to="/gates">Gates</RouterLink>. Retry starts a new run, or queues one when the member
			is still busy.
			Clear takes a failed run off this page; it stays in Runs. Nudge is this page and the sidebar badge — not
			email or Slack. Proposed improvements are not blockers: they live on
			<RouterLink class="underline" to="/self-improvement">Self-Improvement</RouterLink>.
		</p>
		<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
		<p v-if="notice && !error" class="text-muted-foreground text-sm">{{ notice }}</p>
		<ZhPanel v-if="blockers?.draining" as="div" tone="action">
			<p class="text-sm">
				A new version of the application is being deployed. Workers are draining: no queued, pending or blocked run starts, and schedules are paused. Runs in progress will continue to completion. New runs will resume once the deployment is complete.
			</p>
		</ZhPanel>
		<ZhEmptyState v-if="empty && !error">
			Nothing is blocking. A failed or stalled run<template v-if="blockers?.spendEnabled !== false">, paused spend</template>, a missing team lead, or a KPI that needs attention will show up here. Each blocker says what is stuck and why.
			<template #action>
				<Button as-child size="sm"><RouterLink to="/tasks">Go to Tasks</RouterLink></Button>
			</template>
		</ZhEmptyState>
		<template v-if="blockers && counts">
			<section v-if="blockers.capacity?.length" class="space-y-3">
				<BlockerHeading title="No LLM capacity" :count="counts.capacity" />
				<ZhPanel
					v-for="card in capacityCards"
					:key="card.connectionId"
					as="article"
					tone="failed"
					class="flex items-center justify-between gap-3"
				>
					<p class="text-sm font-medium">{{ card.sentence }}</p>
					<Button v-if="card.fix?.to" as-child size="sm" variant="outline" class="shrink-0">
						<RouterLink :to="card.fix.to">{{ card.fix.label }}</RouterLink>
					</Button>
				</ZhPanel>
			</section>
			<section v-if="blockers.lostConnections?.length" class="space-y-3">
				<BlockerHeading title="AI account connections to fix" :count="counts.lostConnections" />
				<ZhPanel
					v-for="connection in blockers.lostConnections"
					:key="connection.connectionId"
					as="article"
					tone="failed"
					class="flex items-center justify-between gap-3"
				>
					<div v-if="connection.reasonCode" class="space-y-1" data-testid="blocked-connection">
						<p class="text-sm font-medium">AI account connection blocked: {{ connection.name }}</p>
						<p class="text-muted-foreground text-sm">{{ blockedConnectionWhose(connection) }}</p>
						<p v-if="connection.reason" class="text-sm">{{ connection.reason }}</p>
						<p v-if="connection.step" class="text-sm"><span class="font-medium">To fix:</span> {{ connection.step }}</p>
						<p class="text-muted-foreground text-sm">
							Runs that use it are paused, and carry on by themselves once the connection is saved again.
						</p>
					</div>
					<div v-else>
						<p class="text-sm font-medium">AI account connection lost: {{ connection.name }}</p>
						<p class="text-muted-foreground text-sm">
							{{ lostLogin(connection.provider) }}<template v-if="connection.name !== connection.ownerName"> ({{ connection.ownerName }})</template>. Runs that use it are blocked until it is reconnected.
						</p>
					</div>
					<RouterLink
						class="shrink-0 text-sm underline"
						:to="{ path: '/agents', query: { reconnect: connection.connectionId } }"
					>
						Reconnect
					</RouterLink>
				</ZhPanel>
			</section>
			<!-- #431: a finding a run sent to another team that no single team could take: the whole of it, for a person. -->
			<section v-if="blockers.raised?.length" class="space-y-3" data-testid="blockers-raised">
				<div class="flex items-center justify-between gap-3">
					<BlockerHeading title="Findings no team could take" :count="counts.raised" />
					<ZhConfirmButton
						label="Clear all"
						confirm-label="Clear findings"
						:title="`Clear all ${findingCount(counts.raised)} from Blockers?`"
						description="Clearing takes them off Blockers and adds nothing to any list. To clear only the ones you have dealt with, use Clear on each."
						:disabled="busy === 'clear-raised'"
						@confirm="clearRaised()"
					/>
				</div>
				<ZhPanel v-for="entry in blockers.raised" :key="entry.id" as="article" tone="action" class="space-y-1">
					<div class="flex items-start justify-between gap-3">
						<p class="text-sm font-medium">{{ entry.title }} <span class="text-muted-foreground font-normal">· {{ entry.kind }}</span></p>
						<Button
							type="button"
							size="sm"
							variant="ghost"
							class="shrink-0"
							:data-testid="`clear-raised-${entry.id}`"
							:disabled="busy === `raised:${entry.id}`"
							@click="clearRaisedOne(entry.id)"
						>
							Clear
						</Button>
					</div>
					<p class="text-sm">{{ raisedWhy(entry) }}</p>
					<p class="text-sm font-medium" data-testid="raised-next">{{ raisedNext(entry) }}</p>
					<p v-if="entry.body" class="text-muted-foreground whitespace-pre-wrap text-sm">{{ entry.body }}</p>
					<ul v-if="entry.links.length" class="space-y-0.5 text-xs">
						<li v-for="link in entry.links" :key="link"><a :href="link" target="_blank" rel="noopener noreferrer" class="text-primary break-all underline underline-offset-2">{{ link }}</a></li>
					</ul>
					<p class="text-muted-foreground text-xs">
						from {{ entry.from.teamName }} ·
						<RouterLink :to="`/runs/${entry.from.runId}`" class="underline underline-offset-2">run {{ entry.from.runId.slice(0, 8) }}</RouterLink>
					</p>
				</ZhPanel>
			</section>
			<section v-if="blockers.models?.length" class="space-y-3">
				<BlockerHeading title="Models unavailable" :count="counts.models" />
				<ZhPanel v-for="check in blockers.models" :key="check.tier" as="article" tone="failed">
					<p class="text-sm font-medium">{{ check.tier }} tier: {{ check.provider }}/{{ check.model }} does not exist</p>
					<p class="text-muted-foreground text-sm">
						Runs on this tier will block until the model id is changed (for example
						<code>{{ check.provider.toUpperCase() }}_MODEL_{{ check.tier.toUpperCase() }}</code>).
					</p>
				</ZhPanel>
			</section>
			<section v-if="blockers.taskProblems?.length" class="space-y-3">
				<BlockerHeading title="Task definitions" :count="counts.taskProblems" />
				<ZhPanel v-for="problem in blockers.taskProblems" :key="problem.id" as="article" class="space-y-1">
					<RouterLink class="text-sm font-medium underline" :to="`/tasks/${problem.id}`">{{ problem.name }}</RouterLink>
					<DefinitionIssueLine v-for="issue in problem.issues" :key="issue.code + issue.message" class="text-sm" :issue="issue" @cloned="load" />
				</ZhPanel>
			</section>
			<section v-if="counts.teamHealth > 0" class="space-y-3">
				<BlockerHeading title="Team health" :count="counts.teamHealth" />
				<ZhPanel v-for="row in teamHealthRows" :key="row.key" as="article">
					<p class="font-medium">{{ row.message }}</p>
					<p v-if="row.detail" class="text-muted-foreground text-sm">{{ row.detail }}</p>
					<RouterLink v-if="row.team" class="text-sm underline" :to="`/teams/${row.team.id}`">Open team</RouterLink>
				</ZhPanel>
			</section>
			<section v-for="group in runGroups" :key="group.key" class="space-y-3">
				<div class="flex items-center justify-between gap-3">
					<BlockerHeading :title="group.title" :count="counts[group.key]" />
					<ZhConfirmButton
						v-if="group.paged && counts[group.key] > 0"
						label="Clear all"
						confirm-label="Clear blockers"
						:title="`Clear all ${failedTotal} failed runs from Blockers?`"
						description="They stay in Runs. Their work items stay held until you decide whether to finish, drop or requeue them."
						:disabled="busy === 'clear-failed'"
						@confirm="clearAll()"
					/>
					<div v-else-if="group.key === 'blocked'" class="flex shrink-0 gap-2">
						<ZhConfirmButton
							label="Retry all"
							:title="`Retry all ${counts.blocked} blocked runs?`"
							description="Each one starts again as a new run, or queues until its member is free, and the blocked run is closed."
							:disabled="busy === 'bulk-blocked'"
							@confirm="retryAllBlocked()"
						/>
						<ZhConfirmButton
							label="Cancel all"
							confirm-label="Cancel blocked runs"
							:title="`Cancel all ${counts.blocked} blocked runs?`"
							description="None of them will resume. An issue one of them held is released, so intake can pick that work up again. They stay in Runs."
							:disabled="busy === 'bulk-blocked'"
							@confirm="cancelAllBlocked()"
						/>
					</div>
				</div>
				<!--
					Retry on a run whose task declares inputs opens the correction form Run detail offers, here in the
					section it was pressed in. The run stays listed until a retry actually starts and supersedes it.
				-->
				<RetryCorrectionForm
					v-if="retrying?.section === group.key"
					v-model="retryForm"
					:inputs="retrying.inputs"
					:errors="retryErrors"
					:busy="busy === retrying.id"
					@submit="submitRetry"
					@close="retrying = null"
				>
					Starts a new run of {{ retrying.id }} with these inputs. This run stays as it was.
				</RetryCorrectionForm>
				<template v-if="group.paged">
					<DataTable
						storage-key="blockers-failed"
						:columns="runColumns"
						:rows="group.rows as unknown as Record<string, unknown>[]"
						:total="failedTotal"
						:page="failedPage"
						:limit="pageSize"
						:pages="failedPages"
						:searchable="false"
						:stacked-on-mobile="true"
						@update:page="goToPage($event)"
						@update:limit="setPageSize($event)"
					>
						<template #cell-taskName="{ row }">
							<span class="flex flex-col gap-1">
								<span class="font-medium">{{ asMessaged(row).message.task ?? '—' }}</span>
								<span v-if="workedAs(asMessaged(row))" class="text-muted-foreground">
									{{ workedAs(asMessaged(row)) }}
								</span>
								<RouterLink class="text-muted-foreground underline" :to="`/runs/${row.id}`">Open run</RouterLink>
							</span>
						</template>
						<template #cell-reason="{ row }">
							<span class="flex flex-col gap-1">
								<span>{{ asMessaged(row).message.sentence }}</span>
								<span v-if="asMessaged(row).message.detail" class="text-muted-foreground">
									{{ asMessaged(row).message.detail }}
								</span>
							</span>
						</template>
						<template #cell-createdAt="{ value }">
							<span class="tabular-nums">{{ new Date(String(value)).toLocaleString() }}</span>
						</template>
						<template #cell-actions="{ row }">
							<span class="flex justify-end gap-2">
								<template v-for="fix in asMessaged(row).message.fix" :key="fix.label">
									<Button v-if="fix.to" as-child size="sm" variant="outline">
										<RouterLink :to="fix.to">{{ fix.label }}</RouterLink>
									</Button>
									<Button
										v-else
										type="button"
										size="sm"
										variant="outline"
										:disabled="busy === row.id"
										@click.stop="perform(fix, String(row.id), group.key)"
									>
										{{ fix.label }}
									</Button>
								</template>
								<Button
									type="button"
									size="sm"
									variant="ghost"
									:disabled="busy === row.id"
									@click.stop="clearRun(String(row.id))"
								>
									Clear
								</Button>
							</span>
						</template>
					</DataTable>
				</template>
				<template v-else>
					<ul class="space-y-3">
						<ZhPanel
							v-for="run in group.rows"
							:key="run.id"
							as="li"
							class="flex items-center justify-between gap-3"
							:data-testid="unmappedActions(run) ? 'action-unmapped' : undefined"
						>
							<div class="space-y-1">
								<!-- #957: an unmapped action reads as its notice alone, never beside the run's own sentence. -->
								<p class="font-medium">{{ (run.message.notice ?? run.message).sentence }}</p>
								<p v-if="(run.message.notice ?? run.message).detail" class="text-muted-foreground text-sm">{{ (run.message.notice ?? run.message).detail }}</p>
								<p class="text-muted-foreground text-sm">
									<template v-if="workedAs(run)">{{ workedAs(run) }} · </template>
									<RouterLink class="underline" :to="`/runs/${run.id}`">Open run</RouterLink>
								</p>
							</div>
							<!--
								The fixes: a link to where the fix is made (the task's page, Spend, Billing), or Retry and
								Cancel on the run itself. A failed run can also be cleared off the page, and so can a green run
								whose outcome nothing was wired to follow: there is nothing to retry, it did not fail, and once
								its work is finished some other way Clear is what takes it off.
							-->
							<div class="flex shrink-0 gap-2">
								<template v-for="fix in run.message.fix" :key="fix.label">
									<!-- #957: Map it on an unmapped action is the notice's one action, the kit command button. -->
									<ZhButton
										v-if="fix.to && unmappedActions(run)"
										variant="ghost"
										@click="router.push(fix.to)"
									>
										{{ fix.label }}
									</ZhButton>
									<Button v-else-if="fix.to" as-child size="sm" variant="outline">
										<RouterLink :to="fix.to">{{ fix.label }}</RouterLink>
									</Button>
									<Button
										v-else
										type="button"
										size="sm"
										:variant="fix.action === 'cancel' ? 'ghost' : 'outline'"
										:disabled="busy === run.id"
										@click="perform(fix, run.id, group.key)"
									>
										{{ fix.label }}
									</Button>
								</template>
								<Button
									v-if="run.status === 'failed'"
									type="button"
									size="sm"
									variant="ghost"
									:disabled="busy === run.id"
									@click="clearRun(run.id)"
								>
									Clear
								</Button>
								<Button
									v-else-if="stoppedReason(run)"
									type="button"
									size="sm"
									variant="ghost"
									:data-testid="`clear-stopped-${run.id}`"
									:disabled="busy === run.id"
									@click="clearRun(run.id)"
								>
									Clear
								</Button>
							</div>
						</ZhPanel>
					</ul>
				</template>
			</section>
		</template>
	</section>
</template>
